/**
 * Acquires the recording stream, optionally mixing microphone + system audio
 * for telehealth/headphones mode.
 *
 * Returns the stream to pass to RecordingService, a cleanup function
 * that stops shared media tracks and closes the mixing AudioContext,
 * and whether shared media was actually acquired (for UI state reset).
 */

export interface MixedStreamResult {
  stream: MediaStream;
  cleanup: () => void;
  sharedMediaActive: boolean;
}

export async function acquireRecordingStream(
  micStream: MediaStream,
  options: { useSharedMedia: boolean; preferCurrentTab: boolean },
): Promise<MixedStreamResult> {
  if (!options.useSharedMedia) {
    return { stream: micStream, cleanup: () => {}, sharedMediaActive: false };
  }

  try {
    const sharedMedia = await navigator.mediaDevices.getDisplayMedia({
      preferCurrentTab: options.preferCurrentTab,
      selfBrowserSurface: 'include',
      audio: true,
      video: true,
    } as DisplayMediaStreamOptions);

    if (sharedMedia.getAudioTracks().length === 0) {
      // No audio track from display media — fall back to mic only
      sharedMedia.getTracks().forEach(t => t.stop());
      return { stream: micStream, cleanup: () => {}, sharedMediaActive: false };
    }

    // Discard video track — only audio needed
    sharedMedia.getVideoTracks().forEach(t => t.stop());

    // Mix microphone + system audio via AudioContext
    const audioCtx = new AudioContext();
    const micSource = audioCtx.createMediaStreamSource(micStream);
    const sharedSource = audioCtx.createMediaStreamSource(sharedMedia);
    const destination = audioCtx.createMediaStreamDestination();
    micSource.connect(destination);
    sharedSource.connect(destination);

    return {
      stream: destination.stream,
      cleanup: () => {
        sharedMedia.getTracks().forEach(t => t.stop());
        audioCtx.close().catch(() => {});
      },
      sharedMediaActive: true,
    };
  } catch {
    // User cancelled the display picker — fall back to mic only
    return { stream: micStream, cleanup: () => {}, sharedMediaActive: false };
  }
}
